import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { format } from "date-fns";
import { TbChevronDown } from "react-icons/tb";
import { CircularProgress, Table } from "@mui/material";
import { TableFilter } from "../../../../../Components/Molecules/Filters";
import { Pagination } from "../../../../../Components/Molecules/TablePagination";
import useCallAxios from "../../../../../Hooks/useCallAxios";
import { useFilter } from "../../../../../Hooks/useFilter";
import { handleTaxIDInput } from "../../../../../Utils";
import { formatCurrency } from "../../../../../Utils/formatters";
import { getPixStatusMeta } from "../constants";

const GRID_COLS =
  "lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1.5fr)_minmax(5.5rem,0.95fr)_minmax(0,0.85fr)_minmax(0,1.1fr)_minmax(0,1.05fr)]";

const PixStatusBadge = ({ status }) => {
  const meta = getPixStatusMeta(status);

  return (
    <span
      className={`inline-flex w-[5.5rem] shrink-0 items-center justify-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${meta.className}`}
    >
      {meta.label}
    </span>
  );
};

const GridCell = ({ label, children, align = "left", className = "" }) => (
  <div className={`min-w-0 ${className}`}>
    <p
      className={`mb-px text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only ${
        align === "right" ? "text-right" : ""
      }`}
    >
      {label}
    </p>
    <div className={align === "right" ? "text-right" : ""}>{children}</div>
  </div>
);

const HistoryHeaderRow = () => (
  <div
    className="col-span-6 grid-history-header hidden lg:-mx-4 lg:grid lg:grid-cols-subgrid lg:items-center lg:gap-x-4 lg:px-4 lg:min-h-12 lg:py-2"
  >
    <span>{intl.get("pix.pixId")}</span>
    <span>{intl.get("pix.endToEndId")}</span>
    <span>{intl.get("grid.status")}</span>
    <span className="text-right">{intl.get("grid.value")}</span>
    <span>{intl.get("grid.createdAt")}</span>
    <div className="flex items-center justify-end gap-2.5">
      <span className="sr-only">{intl.get("common.details")}</span>
      <div className="invisible shrink-0" aria-hidden="true">
        <button type="button" className="btn-outline-icon shrink-0" tabIndex={-1}>
          <TbChevronDown className="h-4 w-4" />
        </button>
      </div>
    </div>
  </div>
);

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return format(Date.parse(value), "dd/MM/yyyy 'às' HH:mm");
  } catch {
    return "—";
  }
};

const formatId = (value) => {
  if (!value) return "—";
  if (value.length <= 24) return value;
  return `${value.slice(0, 12)}…${value.slice(-8)}`;
};

const TransactionDetails = ({ transaction, variant }) => {
  const partyLabel = variant === "received" ? intl.get("pix.payer") : intl.get("pix.payee");
  const party = transaction?.payer;

  return (
    <div className="mt-3 grid gap-3 border-t border-gray-100 pt-3 sm:grid-cols-2">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          {partyLabel}
        </p>
        <p className="text-sm font-medium text-brand-dark">{party?.name || "—"}</p>
        <p className="text-xs text-gray-500">
          Documento: {party?.document ? handleTaxIDInput(party.document) : "—"}
        </p>
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          QR Code
        </p>
        <p className="truncate text-sm text-gray-600" title={transaction?.qrcode || ""}>
          {transaction?.qrcode || "—"}
        </p>
        <p className="mt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          ID externo
        </p>
        <p className="truncate text-sm text-gray-600" title={transaction?.external_id || ""}>
          {transaction?.external_id || "—"}
        </p>
      </div>
    </div>
  );
};

const HistoryRow = ({ transaction, variant }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className={`flex flex-col gap-2 border-b border-gray-100 px-4 py-2.5 last:border-b-0 lg:col-span-6 lg:grid lg:grid-cols-subgrid lg:items-start lg:gap-x-4 lg:px-0 lg:py-2`}
    >
      <GridCell label={intl.get("pix.pixId")}>
        <p className="truncate text-sm font-medium text-brand-dark" title={transaction.id || ""}>
          {formatId(transaction.id)}
        </p>
      </GridCell>

      <GridCell label={intl.get("pix.endToEndId")}>
        <p
          className="truncate text-sm text-gray-600"
          title={transaction.end_to_end_id || ""}
        >
          {formatId(transaction.end_to_end_id)}
        </p>
      </GridCell>

      <GridCell label={intl.get("grid.status")}>
        <PixStatusBadge status={transaction.status} />
      </GridCell>

      <GridCell label={intl.get("grid.value")} align="right">
        <p className="text-sm font-semibold text-brand-dark">
          {formatCurrency(transaction.amount)}
        </p>
      </GridCell>

      <GridCell label={intl.get("grid.createdAt")}>
        <p className="text-sm text-gray-600">{formatDate(transaction.created_at)}</p>
      </GridCell>

      <div className="flex flex-wrap items-center gap-2.5 lg:justify-end">
        <p className="mb-px w-full text-[11px] font-semibold uppercase tracking-wide text-gray-400 lg:sr-only">
          {intl.get("common.details")}
        </p>
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="btn-outline-icon shrink-0"
          aria-expanded={expanded}
          aria-label={expanded ? intl.get("pix.hideDetails") : intl.get("pix.showDetails")}
        >
          <TbChevronDown className={`h-4 w-4 transition ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>

      {expanded && (
        <div className="lg:col-span-6">
          <TransactionDetails transaction={transaction} variant={variant} />
        </div>
      )}
    </div>
  );
};

export const PixSalesHistoryTable = ({ filterTable, apiContext, variant }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [transactions, setTransactions] = useState({});

  const { getFilterObj } = useFilter();
  const filterObj = getFilterObj(filterTable);

  const {
    fetchGET,
    requestLoading: loading,
    requestData,
  } = useCallAxios({ context: apiContext, responseType: "" });

  const handleSetToFirstPage = () => setPage(0);

  const handleChangePage = (_event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    fetchGET({
      queryParameters: {
        pageSize: rowsPerPage,
        page: page + 1,
        ...filterObj,
      },
    });
  }, [filterObj, rowsPerPage, page, apiContext]);

  useEffect(() => {
    setTransactions(requestData);
  }, [requestData]);

  const items = transactions?.data?.items ?? [];
  const rowCount = transactions?.data?.row_count ?? 0;

  return (
    <div>
      <TableFilter
        tableName={filterTable}
        setPageToFirst={handleSetToFirstPage}
      />

      {loading.GET ? (
        <div className="app-card flex items-center justify-center py-12">
          <CircularProgress size={28} />
        </div>
      ) : items.length === 0 ? (
        <div className="app-card overflow-hidden">
          <div className="app-empty-state m-4">Nenhuma transação encontrada.</div>
        </div>
      ) : (
        <div className="app-card overflow-hidden">
          <div className={`lg:grid ${GRID_COLS} lg:gap-x-4 lg:px-4`}>
            <HistoryHeaderRow />
            {items.map((transaction, index) => (
              <HistoryRow
                key={transaction.id || index}
                transaction={transaction}
                variant={variant}
              />
            ))}
          </div>
          <div className="border-t border-gray-100">
            <Table>
              <Pagination
                rowsPerPageOptions={[10, 25, 40]}
                rowCount={rowCount}
                rowsPerPage={rowsPerPage}
                page={page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                label={intl.get("pix.transactionsPerPage")}
              />
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};
